import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectTrigger, SelectValue, SelectItem } from "@/components/ui/select";

export default function JoinNetwork() {
  const { t, lang } = useLang();
  const [reg, setReg] = useState({ full_name: "", email: "", professional_title: "", professional_field: "", country: "", jurisdictions_of_work: "",
    organization: "", years_experience: 0, interested_in_pilot: false,
    interested_in_training: false, interested_in_collaboration: false,
    consent_program: false, consent_marketing: false });
  const [train, setTrain] = useState({ full_name: "", email: "", professional_field: "", country: "", organization: "", training_type: "introductory", consent_program: false });
  const [collab, setCollab] = useState({ full_name: "", email: "", organization: "", country: "", collaboration_type: "research", description: "", consent_program: false });

  const submitReg = async (e) => {
    e.preventDefault();
    if (!reg.consent_program) return toast.error(t.consent.required);
    try { await api.post("/register", { ...reg, language: lang }); toast.success("Registered."); }
    catch { toast.error("Registration failed"); }
  };
  const submitTrain = async (e) => {
    e.preventDefault();
    if (!train.consent_program) return toast.error(t.consent.required);
    try { await api.post("/training-interest", { ...train, language: lang }); toast.success("Training interest recorded."); }
    catch { toast.error("Submission failed"); }
  };
  const submitCollab = async (e) => {
    e.preventDefault();
    if (!collab.consent_program) return toast.error(t.consent.required);
    try { await api.post("/collaboration", { ...collab, language: lang }); toast.success("Collaboration request received."); }
    catch { toast.error("Submission failed"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20" data-testid="join-page">
      <h1 className="text-4xl sm:text-5xl font-bold">{t.cta.join_network}</h1>

      <Tabs defaultValue="register" className="mt-10">
        <TabsList className="bg-[#0A1224] border border-slate-800">
          <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
          <TabsTrigger value="training" data-testid="tab-training">{t.cta.request_training}</TabsTrigger>
          <TabsTrigger value="collab" data-testid="tab-collab">{t.cta.collaborate}</TabsTrigger>
        </TabsList>

        <TabsContent value="register">
          <form className="mt-6 card-mift p-6 grid gap-4" onSubmit={submitReg} data-testid="register-form">
            {[
              { k: "full_name", label: t.forms.full_name, req: true },
              { k: "email", label: t.forms.email, req: true, type: "email" },
              { k: "professional_field", label: t.forms.professional_field, req: true },
              { k: "country", label: t.forms.country, req: true },
              { k: "organization", label: t.forms.organization },
            ].map((fd) => (
              <div key={fd.k}>
                <Label className="text-slate-300">{fd.label}{fd.req && " *"}</Label>
                <Input required={fd.req} type={fd.type || "text"} data-testid={`reg-${fd.k}`}
                  className="mt-1 bg-[#0A1224] border-slate-700 text-slate-100"
                  value={reg[fd.k]} onChange={(e) => setReg((s) => ({ ...s, [fd.k]: e.target.value }))} />
              </div>
            ))}
            <div className="flex flex-col gap-2">
              {[["interested_in_pilot", t.forms.interested_pilot], ["interested_in_training", t.forms.interested_training], ["interested_in_collab", t.forms.interested_collab]]
                .map(([k, l], i) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-slate-300">
                    <Checkbox data-testid={`reg-${k}`} checked={reg[k === "interested_in_collab" ? "interested_in_collaboration" : k]}
                      onCheckedChange={(v) => setReg((s) => ({ ...s, [k === "interested_in_collab" ? "interested_in_collaboration" : k]: !!v }))} />
                    {l}
                  </label>
                ))}
              <label className="flex items-start gap-2 text-sm text-slate-300 mt-2">
                <Checkbox data-testid="reg-consent" checked={reg.consent_program} onCheckedChange={(v) => setReg((s) => ({ ...s, consent_program: !!v }))} />
                <span>{t.consent.program}</span>
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-400 mt-1">
                <Checkbox data-testid="reg-consent-marketing" checked={reg.consent_marketing} onCheckedChange={(v) => setReg((s) => ({ ...s, consent_marketing: !!v }))} />
                <span>{t.consent.marketing}</span>
              </label>
            </div>
            <button className="btn-primary w-max" data-testid="reg-submit">Register</button>
          </form>
        </TabsContent>

        <TabsContent value="training">
          <form className="mt-6 card-mift p-6 grid gap-4" onSubmit={submitTrain} data-testid="training-form">
            {[
              { k: "full_name", label: t.forms.full_name, req: true },
              { k: "email", label: t.forms.email, req: true, type: "email" },
              { k: "professional_field", label: t.forms.professional_field, req: true },
              { k: "country", label: t.forms.country, req: true },
              { k: "organization", label: t.forms.organization },
            ].map((fd) => (
              <div key={fd.k}>
                <Label className="text-slate-300">{fd.label}{fd.req && " *"}</Label>
                <Input required={fd.req} type={fd.type || "text"} data-testid={`train-${fd.k}`}
                  className="mt-1 bg-[#0A1224] border-slate-700 text-slate-100"
                  value={train[fd.k]} onChange={(e) => setTrain((s) => ({ ...s, [fd.k]: e.target.value }))} />
              </div>
            ))}
            <div><Label className="text-slate-300">{t.forms.training_type} *</Label>
              <Select value={train.training_type} onValueChange={(v) => setTrain((s) => ({ ...s, training_type: v }))}>
                <SelectTrigger data-testid="train-type" className="mt-1 bg-[#0A1224] border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A1224] border-slate-700 text-slate-100">
                  <SelectItem value="introductory">Introductory</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                  <SelectItem value="institutional">Institutional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-300">
              <Checkbox data-testid="train-consent" checked={train.consent_program} onCheckedChange={(v) => setTrain((s) => ({ ...s, consent_program: !!v }))} />
              <span>{t.consent.program}</span>
            </label>
            <button className="btn-primary w-max" data-testid="train-submit">{t.cta.request_training}</button>
          </form>
        </TabsContent>

        <TabsContent value="collab">
          <form className="mt-6 card-mift p-6 grid gap-4" onSubmit={submitCollab} data-testid="collab-form">
            {[
              { k: "full_name", label: t.forms.full_name, req: true },
              { k: "email", label: t.forms.email, req: true, type: "email" },
              { k: "organization", label: "Organization *", req: true },
              { k: "country", label: t.forms.country, req: true },
            ].map((fd) => (
              <div key={fd.k}>
                <Label className="text-slate-300">{fd.label}</Label>
                <Input required={fd.req} type={fd.type || "text"} data-testid={`collab-${fd.k}`}
                  className="mt-1 bg-[#0A1224] border-slate-700 text-slate-100"
                  value={collab[fd.k]} onChange={(e) => setCollab((s) => ({ ...s, [fd.k]: e.target.value }))} />
              </div>
            ))}
            <div><Label className="text-slate-300">{t.forms.collaboration_type} *</Label>
              <Select value={collab.collaboration_type} onValueChange={(v) => setCollab((s) => ({ ...s, collaboration_type: v }))}>
                <SelectTrigger data-testid="collab-type" className="mt-1 bg-[#0A1224] border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A1224] border-slate-700 text-slate-100">
                  <SelectItem value="research">Research</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="publication">Publication</SelectItem>
                  <SelectItem value="pilot_partner">Pilot partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300">{t.forms.description} *</Label>
              <Textarea required rows={4} data-testid="collab-desc"
                className="mt-1 bg-[#0A1224] border-slate-700 text-slate-100"
                value={collab.description} onChange={(e) => setCollab((s) => ({ ...s, description: e.target.value }))} />
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-300">
              <Checkbox data-testid="collab-consent" checked={collab.consent_program} onCheckedChange={(v) => setCollab((s) => ({ ...s, consent_program: !!v }))} />
              <span>{t.consent.program}</span>
            </label>
            <button className="btn-primary w-max" data-testid="collab-submit">{t.cta.collaborate}</button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}
